import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import { addTeamRepo } from '@/apis/addTeamRepo';
import { useMutation } from '@tanstack/react-query';
import { fetchTeams } from '@/apis/fetchTeams';
import { useUserLogged } from '@/hooks/useUserLogged';

export type Team = {
  TeamName: string;
  TeamUUID: string;
  OrgUUID: string;
  id: string;
  uuid: string;
};

interface TeamState {
  team: Team | null;
  teams: Team[];
  setTeams: (teams: Team[]) => void;
  setTeam: (team: Team) => void;
  clearTeam: () => void;
}

const useTeamStore = create<TeamState>((set) => ({
  team: null,
  teams: [],
  setTeams: (teams) => set({ teams }),
  setTeam: (team) => set({ team }),
  clearTeam: () => set({ team: null }),
}));

export const useTeams = () => {
  const setTeams = useTeamStore((state) => state.setTeams);
  const teams = useTeamStore((state) => state.teams);
  const { user, organization } = useUserLogged();

  // Get OrgUUID from both sources to ensure we always have a value if available
  const orgUUID = user?.OrganizationUUID || organization?.OrgUUID;

  return useQuery({
    // Include both IDs in query key for better tracking
    queryKey: ['FetchTeams', user?.OrganizationUUID, organization?.OrgUUID],
    queryFn: async () => {
      if (!orgUUID) {
        console.warn('[useTeams] Query running but no OrgUUID available');
        return [];
      }

      try {
        const response = await fetchTeams({ OrgUUID: orgUUID });
        if (response?.Teams?.length) {
          setTeams(response.Teams);
          return response.Teams;
        } else {
          return [];
        }
      } catch (error) {
        console.error('[useTeams] Error fetching teams:', error);
        return [];
      }
    },
    enabled: !!orgUUID,
    initialData: teams.length > 0 ? teams : undefined,
  });
};

const useAddTeamRepo = () => {
  return useMutation({
    mutationFn: async ({
      teamUUID,
      repoUUID,
    }: {
      teamUUID: string;
      repoUUID: string;
    }) => addTeamRepo(teamUUID, repoUUID),
  });
};

export default useTeamStore;
